import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    Row, Col, Form, FormGroup, Label, Input,
    InputGroup, InputGroupText, Button, Spinner, FormFeedback, Collapse
} from "reactstrap";

import { useSettingsAuth } from "../hooks/useSettingsAuth";
import { useProfileUser } from "../hooks/useProfileUser";
import { useProfileUpdate } from "../hooks/useProfileUpdate";
import { RoleBadges, translateRole } from "../../../constants/roleDisplay";
import PhoneInput from "../../../components/Forms/PhoneInput";

/**
 * Ayarlar → Kullanıcı Bilgileri.
 *
 * Kaydetme mantığı useProfileUpdate içindedir ve bu dosyada değiştirilmemiştir:
 * yalnız değişen alanlar gönderilir, şifre alanları boşsa şifre değişmez.
 *
 * Tasarım taslağındaki üç öğe KASITLI olarak yer almaz, çünkü sistemde karşılıkları
 * yoktur ve sabit değer yazmak kullanıcıya yanlış bilgi verirdi:
 *  - "E-posta Doğrulama · Doğrulandı": emailVerified alanı yok. (`enabled` alanı
 *    hesabın aktifliğidir, e-posta doğrulaması değildir.)
 *  - "Aktif Oturumlar · N oturum": oturum envanteri/listeleme ucu yok.
 * Backend desteği geldiğinde eklenecektir.
 */

const styles = `
  .up-page { --up-navy: #0A316E; --up-border: #e7eaf0; }

  .up-card {
      background: #fff;
      border: 1px solid var(--up-border);
      border-radius: 1rem;
      padding: 1.25rem 1.5rem;
      height: 100%;
  }
  .up-card-title {
      display: flex; align-items: center; gap: .6rem;
      font-weight: 600; font-size: 1.02rem; margin-bottom: 1.25rem;
  }
  .up-card-icon {
      width: 34px; height: 34px; flex: 0 0 34px;
      border-radius: .6rem; background: #EEF3FB; color: #221C5D;
      display: flex; align-items: center; justify-content: center; font-size: 1.1rem;
  }

  /* Üst özet kartı */
  .up-summary {
      background: #fff; border: 1px solid var(--up-border);
      border-radius: 1rem; padding: 1.25rem 1.5rem; margin-bottom: 1rem;
  }
  .up-meta {
      display: flex; flex-wrap: wrap; gap: 1.5rem; margin-top: 1rem;
      padding-top: 1rem; border-top: 1px solid var(--up-border);
  }
  .up-meta-item { display: flex; align-items: center; gap: .6rem; }
  .up-meta-icon { color: #8a94a6; font-size: 1.1rem; }
  .up-meta-label { font-size: .72rem; color: #8a94a6; text-transform: uppercase; letter-spacing: .03em; }
  .up-meta-value { font-size: .875rem; color: #1f2937; font-weight: 500; }

  /* Güvenlik satırları */
  .up-sec-row {
      display: flex; align-items: center; gap: .9rem;
      padding: .9rem 0; border-bottom: 1px solid var(--up-border);
  }
  .up-sec-row:last-of-type { border-bottom: none; }
  .up-sec-icon {
      width: 40px; height: 40px; flex: 0 0 40px; border-radius: .7rem;
      display: flex; align-items: center; justify-content: center; font-size: 1.2rem;
  }
  .up-sec-icon--slate { background: #F1F4F9; color: #221C5D; }
  .up-sec-icon--green { background: #EAF7EE; color: #28a745; }
  .up-sec-title { font-weight: 600; font-size: .9rem; color: #1f2937; }
  .up-sec-text  { font-size: .8rem; color: #8a94a6; }

  .up-help {
      display: flex; align-items: center; gap: .9rem;
      background: #F7F9FC; border-radius: .8rem; padding: 1rem; margin-top: 1rem;
  }

  .up-locked-icon { color: #b6bdc9; }


  /* Avatar */
  .up-avatar-wrap { flex: 0 0 84px; }
  .up-avatar {
      width: 84px; height: 84px; border-radius: 1rem;
      background: #EEF3FB; color: #221C5D;
      display: flex; align-items: center; justify-content: center;
      font-size: 1.6rem; font-weight: 700; overflow: hidden;
      border: 1px solid var(--up-border);
  }
  .up-page .up-help-link {
      color: var(--up-navy); font-weight: 600; font-size: .85rem; text-decoration: none;
  }
  .up-page .up-help-link:hover { text-decoration: underline; }

  .up-save-bar { display: flex; justify-content: flex-end; margin-top: 1rem; }

  /* Global custom.css'te btn-navy (dolu) var ama outline karşılığı yok;
     taslaktaki mavi çerçeveli buton için yerel karşılığı. */
  .up-page .up-btn-navy-outline {
      background-color: transparent;
      border: 1px solid var(--up-navy);
      color: var(--up-navy);
      transition: all .2s ease;
  }
  .up-page .up-btn-navy-outline:hover,
  .up-page .up-btn-navy-outline:focus {
      background-color: var(--up-navy);
      color: #fff;
  }
`;

const formatDateTime = (v) => {
    if (!v) return "-";
    const d = new Date(v);
    if (isNaN(d.getTime())) return "-";
    return `${d.toLocaleDateString("tr-TR", { day: "numeric", month: "long", year: "numeric" })} - ${d.toLocaleTimeString("tr-TR", { hour: "2-digit", minute: "2-digit" })}`;
};

const MetaItem = ({ icon, label, value }) => (
    <div className="up-meta-item">
        <i className={`bx ${icon} up-meta-icon`} />
        <div>
            <div className="up-meta-label">{label}</div>
            <div className="up-meta-value">{value || "-"}</div>
        </div>
    </div>
);

export default function UserProfileTab() {
    useSettingsAuth();
    const navigate = useNavigate();
    const { me, roles, loadingUser, refetchMe } = useProfileUser();
    const {
        formik,
        loading,
        showOld, setShowOld,
        showNew, setShowNew,
        showConfirm, setShowConfirm
    } = useProfileUpdate(me, refetchMe);

    // Şifre alanları taslakta olduğu gibi bir butonun arkasında durur; açıldığında
    // aynı formik alanları kullanılır, kaydetme davranışı değişmez.
    const [passwordOpen, setPasswordOpen] = useState(false);

    if (loadingUser) {
        return <div className="py-5 text-center"><Spinner /> Yükleniyor...</div>;
    }

    if (!me) {
        return <div className="py-5 text-center text-danger">Kullanıcı bilgisi alınamadı.</div>;
    }

    const primaryRole = (roles || [])[0];
    const primaryRoleLabel = primaryRole ? translateRole(primaryRole) : "-";

    // Destek yalnız süper admin ve firma rollerine açıktır (bkz. SidebarContent ve
    // TicketController). Erişimi olmayan cari/bayi kullanıcısına bağlantı gösterilmez;
    // gösterilseydi tıklayınca yetkisiz sayfaya düşerdi.
    const canUseSupport = (roles || []).some(r =>
        ["ROLE_SUPER_ADMIN", "ROLE_COMPANY_ADMIN", "ROLE_COMPANY_ACCOUNTANT",
         "ROLE_COMPANY_SALESPERSON", "ROLE_COMPANY_CASHIER"].includes(r));
    // Profil alanında gösterilecek baş harfler.
    const initials = (me.username || me.email || "?")
        .trim().split(/\s+/).slice(0, 2).map(w => w[0]).join("").toUpperCase();

    const orgLabel = me.companyName ? "Şirket" : "Cari/Bayi Adı";
    const orgValue = me.companyName || me.accountName || "-";

    // 2FA rozeti kaydedilmemiş seçimi de yansıtır; kullanıcı neyi kaydedeceğini görür.
    const twoFaOn = !!formik.values.twoFaEnabled;
    const twoFaDirty = twoFaOn !== !!me.twoFaEnabled;

    return (
        <div className="user-profile-page up-page">
            <style>{styles}</style>

            <Form onSubmit={formik.handleSubmit}>

                {/* ── Özet kartı ─────────────────────────────────────── */}
                <div className="up-summary">
                    <div className="d-flex align-items-start gap-3">
                        <div className="up-avatar-wrap">
                            <div className="up-avatar">
                                <span>{initials}</span>
                            </div>
                        </div>

                        <div className="flex-grow-1">
                            <div className="d-flex flex-wrap align-items-center gap-2">
                                <h5 className="mb-0">{me.username || "-"}</h5>
                                {/* Rozet adı ve rengi Kullanıcılar ekranıyla ortak modülden gelir. */}
                                <RoleBadges roles={roles || []} />
                            </div>
                            <div className="text-muted mt-1">{orgValue}</div>
                        </div>
                    </div>

                    <div className="up-meta">
                        {me.companyCode && (
                            <MetaItem icon="bx-id-card" label="Firma Kodu" value={me.companyCode} />
                        )}
                        <MetaItem icon="bx-envelope" label="E-posta" value={me.email} />
                        {/* Telefon kaydedildiğinde bu satırda görünür; boşsa yer kaplamaz. */}
                        {me.phoneNumber && (
                            <MetaItem icon="bx-phone" label="Telefon" value={me.phoneNumber} />
                        )}
                        <MetaItem icon="bx-user-pin" label="Rol" value={primaryRoleLabel} />
                        <MetaItem icon="bx-time-five" label="Son güncelleme" value={formatDateTime(me.updatedAt)} />
                    </div>
                </div>

                <Row className="g-3">
                    {/* ── Profil Bilgileri ───────────────────────────── */}
                    <Col lg={6}>
                        <div className="up-card">
                            <div className="up-card-title">
                                <span className="up-card-icon"><i className="bx bx-user" /></span>
                                Profil Bilgileri
                            </div>

                            <FormGroup className="mb-3">
                                <Label>Kullanıcı Adı</Label>
                                <Input
                                    type="text"
                                    {...formik.getFieldProps("username")}
                                    invalid={formik.touched.username && !!formik.errors.username}
                                />
                                {formik.touched.username && formik.errors.username && (
                                    <FormFeedback className="d-block">{formik.errors.username}</FormFeedback>
                                )}
                            </FormGroup>

                            <PhoneInput
                                label="Telefon Numarası"
                                value={formik.values.phoneNumber}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                name="phoneNumber"
                                invalid={formik.touched.phoneNumber && !!formik.errors.phoneNumber}
                                errorText={formik.errors.phoneNumber}
                            />

                            {/* E-posta ve şirket bu ekrandan değiştirilemez; kilit ikonu
                                bunu görünür kılar. */}
                            <FormGroup className="mb-3">
                                <Label>E-posta</Label>
                                <InputGroup>
                                    <Input type="email" value={me.email || "-"} disabled className="bg-light" />
                                    <InputGroupText className="bg-light">
                                        <i className="bx bx-lock-alt up-locked-icon" />
                                    </InputGroupText>
                                </InputGroup>
                            </FormGroup>

                            <FormGroup className="mb-0">
                                <Label>{orgLabel}</Label>
                                <InputGroup>
                                    <Input type="text" value={orgValue} disabled className="bg-light" />
                                    <InputGroupText className="bg-light">
                                        <i className="bx bx-lock-alt up-locked-icon" />
                                    </InputGroupText>
                                </InputGroup>
                            </FormGroup>

                        </div>
                    </Col>

                    {/* ── Güvenlik Merkezi ───────────────────────────── */}
                    <Col lg={6}>
                        <div className="up-card">
                            <div className="up-card-title">
                                <span className="up-card-icon"><i className="bx bx-shield-quarter" /></span>
                                Güvenlik Merkezi
                            </div>

                            {/* Soldaki "Kullanıcı Adı" etiketiyle birebir aynı yüksekliği
                                kaplayan görünmez hizalayıcı: kartın tüm içeriği bir etiket
                                boyu aşağı kayar ve mavi bilgi kutusu, kullanıcı adı
                                kutusuyla aynı hizada başlar. Sabit piksel yerine gerçek
                                Label kullanıldı ki tema yazı boyutu değişirse hiza bozulmasın.
                                Kartlar lg altında alt alta düştüğü için orada gösterilmez. */}
                            <Label className="invisible d-none d-lg-block" aria-hidden="true">&nbsp;</Label>

                            <div className="alert alert-info py-2 small mb-3">
                                <i className="bx bx-info-circle me-1" />
                                Hesabınızın güvenlik durumunu aşağıda görebilirsiniz.
                            </div>

                            {/* Şifre */}
                            <div className="up-sec-row">
                                <span className="up-sec-icon up-sec-icon--slate"><i className="bx bx-lock-alt" /></span>
                                <div className="flex-grow-1">
                                    <div className="up-sec-title">Şifre</div>
                                    <div className="up-sec-text">Şifrenizi düzenli olarak değiştirin.</div>
                                </div>
                                <Button
                                    type="button"
                                    size="sm"
                                    className="btn-rounded up-btn-navy-outline"
                                    onClick={() => setPasswordOpen(o => !o)}
                                >
                                    {passwordOpen ? "Vazgeç" : "Şifreyi Değiştir"}
                                </Button>
                            </div>

                            <Collapse isOpen={passwordOpen}>
                                <div className="p-3 bg-light rounded-3 border mb-3">
                                    <p className="text-muted small mb-3">
                                        <i className="bx bx-info-circle me-1" />
                                        Alanları boş bırakırsanız şifreniz değişmez.
                                    </p>

                                    <FormGroup className="mb-3">
                                        <Label>Mevcut Şifre</Label>
                                        <InputGroup>
                                            <Input
                                                type={showOld ? "text" : "password"}
                                                {...formik.getFieldProps("oldPassword")}
                                                invalid={formik.touched.oldPassword && !!formik.errors.oldPassword}
                                                placeholder="Mevcut şifreniz"
                                            />
                                            <InputGroupText onClick={() => setShowOld(!showOld)} style={{ cursor: "pointer" }}>
                                                <i className={`bx bx-${showOld ? "show" : "hide"}`} />
                                            </InputGroupText>
                                            <FormFeedback className={formik.touched.oldPassword && !!formik.errors.oldPassword ? "d-block" : "d-none"}>
                                                {formik.errors.oldPassword}
                                            </FormFeedback>
                                        </InputGroup>
                                    </FormGroup>

                                    <FormGroup className="mb-3">
                                        <Label>Yeni Şifre</Label>
                                        <InputGroup>
                                            <Input
                                                type={showNew ? "text" : "password"}
                                                {...formik.getFieldProps("newPassword")}
                                                invalid={formik.touched.newPassword && !!formik.errors.newPassword}
                                                placeholder="Yeni şifreniz"
                                            />
                                            <InputGroupText onClick={() => setShowNew(!showNew)} style={{ cursor: "pointer" }}>
                                                <i className={`bx bx-${showNew ? "show" : "hide"}`} />
                                            </InputGroupText>
                                            <FormFeedback className={formik.touched.newPassword && !!formik.errors.newPassword ? "d-block" : "d-none"}>
                                                {formik.errors.newPassword}
                                            </FormFeedback>
                                        </InputGroup>
                                    </FormGroup>

                                    <FormGroup className="mb-0">
                                        <Label>Yeni Şifre (Tekrar)</Label>
                                        <InputGroup>
                                            <Input
                                                type={showConfirm ? "text" : "password"}
                                                {...formik.getFieldProps("confirmNewPassword")}
                                                invalid={formik.touched.confirmNewPassword && !!formik.errors.confirmNewPassword}
                                                placeholder="Yeni şifreniz (tekrar)"
                                            />
                                            <InputGroupText onClick={() => setShowConfirm(!showConfirm)} style={{ cursor: "pointer" }}>
                                                <i className={`bx bx-${showConfirm ? "show" : "hide"}`} />
                                            </InputGroupText>
                                            <FormFeedback className={formik.touched.confirmNewPassword && !!formik.errors.confirmNewPassword ? "d-block" : "d-none"}>
                                                {formik.errors.confirmNewPassword}
                                            </FormFeedback>
                                        </InputGroup>
                                    </FormGroup>
                                </div>
                            </Collapse>

                            {/* 2FA */}
                            <div className="up-sec-row">
                                <span className={`up-sec-icon ${twoFaOn ? "up-sec-icon--green" : "up-sec-icon--slate"}`}>
                                    <i className="bx bx-shield-alt-2" />
                                </span>
                                <div className="flex-grow-1">
                                    <div className="up-sec-title">İki Adımlı Doğrulama (2FA)</div>
                                    <div className="up-sec-text">Hesap güvenliğinizi artırmak için 2FA kullanın.</div>
                                </div>
                                {/* Anahtar diğer aksiyonlardan dar olduğu için sağ kenara
                                    fazla yaklaşıyordu; biraz sola alınır. */}
                                <div className="d-flex align-items-center me-4">
                                    {/* Eskiden olduğu gibi anahtar; formik değerine bağlı,
                                        kaydetme davranışı değişmez. Durum anahtarın kendi
                                        görünümünden okunduğu için ayrıca rozet yazılmaz. */}
                                    <FormGroup switch className="mb-0">
                                        <Input
                                            id="twoFaSwitch"
                                            type="switch"
                                            role="switch"
                                            className="fs-4"
                                            checked={twoFaOn}
                                            onChange={(e) => formik.setFieldValue("twoFaEnabled", e.target.checked)}
                                        />
                                    </FormGroup>
                                </div>
                            </div>

                            {/* Değişiklik ancak kaydedilince geçerli olur; rozet kaydedilmemiş
                                durumu gösterdiği için bu uyarı gerekli. */}
                            {twoFaDirty && (
                                <div className="text-warning small mt-2">
                                    <i className="bx bx-info-circle me-1" />
                                    2FA değişikliği "Değişiklikleri Kaydet" ile uygulanır.
                                </div>
                            )}

                            {/* Yukarıdaki güvenlik satırlarıyla aynı düzen: solda ikon +
                                metin, sağda aksiyon, dikeyde ortalanmış. */}
                            <div className="up-help">
                                <i className="bx bx-lock-open-alt fs-4 text-muted" />
                                <div className="flex-grow-1">
                                    <div className="up-sec-title">Güvenliğiniz bizim için önemli</div>
                                    <div className="up-sec-text">Hesap güvenliğinizle ilgili yardıma mı ihtiyacınız var?</div>
                                </div>
                                {canUseSupport && (
                                    <Button
                                        type="button"
                                        size="sm"
                                        className="btn-rounded up-btn-navy-outline"
                                        onClick={() => navigate("/support")}
                                    >
                                        Destek <i className="bx bx-right-arrow-alt align-middle" />
                                    </Button>
                                )}
                            </div>
                        </div>
                    </Col>
                </Row>

                <div className="up-save-bar">
                    <Button className="btn-navy btn-rounded px-4" type="submit" disabled={loading}>
                        {loading ? (
                            <><Spinner size="sm" className="me-2" /> Kaydediliyor...</>
                        ) : (
                            <><i className="bx bx-save me-1" /> Değişiklikleri Kaydet</>
                        )}
                    </Button>
                </div>
            </Form>
        </div>
    );
}
